"use client";

import { ReactNode, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { CaretUpIcon, CaretDownIcon } from "@phosphor-icons/react/ssr";

import type { GetStartedStep } from "../types";
import StepPill from "./StepPill";
import StepCard from "./StepCard";
import PreviewCard from "./PreviewCard";
import NavButton from "./NavButton";

type DesktopTabsProps = {
    steps: GetStartedStep[];
    activeIndex: number;
    onSelect: (index: number) => void;
    productBadge?: ReactNode;
};

const DesktopTabs = ({ steps, activeIndex, onSelect, productBadge }: DesktopTabsProps) => {
    const activeStep = steps[activeIndex];
    const hasInteracted = useRef(false);

    const goUp = useCallback(() => {
        hasInteracted.current = true;
        onSelect(Math.max(0, activeIndex - 1));
    }, [activeIndex, onSelect]);

    const goDown = useCallback(() => {
        hasInteracted.current = true;
        onSelect(Math.min(steps.length - 1, activeIndex + 1));
    }, [activeIndex, onSelect, steps.length]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
            e.preventDefault();
            goUp();
        } else if (e.key === "ArrowDown" || e.key === "ArrowRight") {
            e.preventDefault();
            goDown();
        }
    }, [goUp, goDown]);

    const handleSelect = useCallback((index: number) => {
        hasInteracted.current = true;
        onSelect(index);
    }, [onSelect]);

    if (!activeStep) return null;

    const tabId = (id: string) => `gs-tab-${id}`;
    const panelId = (id: string) => `gs-panel-${id}`;

    const renderSteps = () =>
        steps.map((step, index) => {
            if (index === activeIndex) {
                return (
                    <AnimatePresence mode="wait" key={step.id}>
                        <StepCard
                            stepIndex={index}
                            title={step.title}
                            description={step.description}
                            icon={step.icon}
                            id={panelId(step.id)}
                            labelledBy={tabId(step.id)}
                            skipInitialAnimation={!hasInteracted.current}
                        />
                    </AnimatePresence>
                );
            }

            return (
                <StepPill
                    key={step.id}
                    label={step.title}
                    onClick={() => handleSelect(index)}
                    id={tabId(step.id)}
                    controlsId={panelId(step.id)}
                />
            );
        });

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-10">
            {/* Steps column */}
            <div className="flex gap-6 xl:pb-14">
                {/* Nav arrows — xl+ only */}
                <div className="hidden xl:flex flex-col items-center justify-center gap-6 shrink-0 p-2.5">
                    <NavButton icon={CaretUpIcon} onClick={goUp} disabled={activeIndex === 0} aria-label="Previous step" />
                    <NavButton icon={CaretDownIcon} onClick={goDown} disabled={activeIndex === steps.length - 1} aria-label="Next step" />
                </div>

                <div
                    role="tablist"
                    aria-label="Getting started steps"
                    aria-orientation="vertical"
                    onKeyDown={handleKeyDown}
                    className="flex flex-col gap-3 xl:gap-4 flex-1 min-w-0"
                >
                    {renderSteps()}
                </div>
            </div>

            {/* Preview column — single instance */}
            <div className="relative rounded-t-2xl overflow-hidden h-[400px] xl:min-h-[400px] xl:h-auto">
                <PreviewCard
                    steps={steps}
                    activeIndex={activeIndex}
                    productBadge={productBadge}
                />
            </div>
        </div>
    );
};

export default DesktopTabs;
